import { useState } from "react";
import { getHistory, deleteHistoryEntry, set } from "./storage";
import { formatAmount, getItemEmoji, getAnimalAvatar } from "./Expense";

function HistoryEntry({ entry, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);

  const date = new Date(entry.archivedAt);
  const dateStr = date.toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
  });
  const timeStr = date.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const total = entry.items.reduce((sum, item) => sum + (item.price || 0), 0);
  const totalWithTip = total + total * 0.1;
  const itemsWithName = entry.items.filter((i) => i.name.trim());

  const getFriendTotal = (friendId) => {
    let friendTotal = 0;
    entry.items.forEach((item) => {
      const totalDist = entry.splits
        .filter((s) => s.itemId === item.id)
        .reduce((sum, s) => sum + s.amount, 0);
      const cuota = totalDist === 0 ? 0 : item.price / totalDist;
      const contrib =
        entry.splits.find(
          (s) => s.itemId === item.id && s.friendId === friendId
        )?.amount || 0;
      friendTotal += cuota * contrib;
    });
    return friendTotal;
  };

  return (
    <div
      className={`history-entry ${expanded ? "history-entry--expanded" : ""}`}
    >
      <button
        className="history-entry-header"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="history-entry-date">
          <span className="history-date-day">{dateStr}</span>
          <span className="history-date-time">{timeStr}</span>
        </div>
        <div className="history-entry-friends">
          {entry.friends.slice(0, 4).map((friend, i) => (
            <div
              key={friend.id}
              className={`history-avatar friend-avatar-${i % 8}`}
            >
              {friend.animalHex ? (
                <img
                  src={getAnimalAvatar(friend.animalHex)}
                  alt=""
                  className="friend-avatar-img"
                />
              ) : (
                "?"
              )}
              <span className="history-avatar-tooltip">
                {friend.name || "???"}
              </span>
            </div>
          ))}
          {entry.friends.length > 4 && (
            <span className="history-avatar-more">
              +{entry.friends.length - 4}
            </span>
          )}
        </div>
        <div className="history-entry-total">{formatAmount(totalWithTip)}</div>
        <span
          className={`history-chevron ${expanded ? "history-chevron--open" : ""}`}
        >
          ›
        </span>
      </button>

      {expanded && (
        <div className="history-detail">
          <table className="history-table">
            <tbody>
              {itemsWithName.map((item) => {
                const emoji = getItemEmoji(item.name);
                return (
                  <tr key={item.id}>
                    <td className="history-item-emoji">{emoji}</td>
                    <td className="history-item-name">{item.name}</td>
                    <td className="history-item-price">
                      {formatAmount(item.price || 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="history-total-row">
                <td></td>
                <td>Total (con propina)</td>
                <td>{formatAmount(totalWithTip)}</td>
              </tr>
            </tfoot>
          </table>
          {entry.friends.length > 0 && (
            <div className="history-friends-detail">
              {entry.friends.map((friend, i) => {
                const withTip =
                  getFriendTotal(friend.id) * 1.1;
                return (
                  <div key={friend.id} className="history-friend-chip">
                    <div
                      className={`history-friend-chip-avatar friend-avatar-${i % 8}`}
                    >
                      {friend.animalHex ? (
                        <img
                          src={getAnimalAvatar(friend.animalHex)}
                          alt=""
                          className="friend-avatar-img"
                        />
                      ) : (
                        "?"
                      )}
                    </div>
                    <span className="history-friend-chip-name">
                      {friend.name || "???"}
                    </span>
                    <span className="history-friend-chip-amount">
                      {formatAmount(withTip)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="history-entry-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              ✏️ Editar
            </button>
            <button
              className="history-delete"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm("¿Eliminar esta salida del historial?")) {
                  onDelete();
                }
              }}
            >
              Eliminar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function History({ onBack, onEdit }) {
  const [history, setHistory] = useState(() => getHistory());

  const handleDelete = (index) => {
    deleteHistoryEntry(index);
    setHistory(getHistory());
  };

  const handleEdit = (index) => {
    const entry = history[index];
    // Load this expense as the current one
    const { archivedAt, ...expense } = entry;
    set(expense);
    // Remove from history
    deleteHistoryEntry(index);
    setHistory(getHistory());
    onEdit();
  };

  return (
    <div className="history-page">
      <div className="history-header">
        <button className="btn-back" onClick={onBack} title="Volver">
          🍃
        </button>
        <h2 className="expense-title">📋 Historial</h2>
      </div>

      {history.length === 0 ? (
        <div className="history-empty">
          <span className="history-empty-icon">🏝️</span>
          <p className="history-empty-text">
            Aún no tienes salidas guardadas.
          </p>
          <p className="history-empty-sub">
            Cuando inicies una nueva salida, la anterior se guarda acá.
          </p>
        </div>
      ) : (
        <div className="history-list">
          {history.map((entry, index) => (
            <HistoryEntry
              key={entry.archivedAt + index}
              entry={entry}
              onDelete={() => handleDelete(index)}
              onEdit={() => handleEdit(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
